/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons';

import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Avatar } from '@/primitives/Avatar';
import { Button } from '@/primitives/Button';
import Tail from '@/primitives/Icons/Tail.svg?react';
import { Text } from '@/primitives/Text';
import { tokens } from '@/theme';

import { AlertWizardStep } from './constants';

import './CreateAlertWizardHeader.css';

type CreateAlertWizardHeaderProps = {
  currentStep: AlertWizardStep;
  onClose: () => void;
};

function getStepIndex(step: AlertWizardStep): number {
  const order: AlertWizardStep[] = ['setup-connection', 'set-parameters', 'review'];
  return order.indexOf(step);
}

export function CreateAlertWizardHeader({ currentStep, onClose }: CreateAlertWizardHeaderProps) {
  const { t } = useMimicTranslation('notificationManagement');
  const currentIndex = getStepIndex(currentStep);

  const stepLabels = [t('steps.setupConnection'), t('steps.setParameters'), t('steps.review')];

  return (
    <div className="alert-wizard-status">
      <div className="alert-wizard-status-step-container">
        {stepLabels.map((label, index) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="alert-wizard-step">
              {index < currentIndex ? (
                <CheckCircleFilled style={{ fontSize: '2rem' }} />
              ) : (
                <Avatar
                  size="sm"
                  text={String(index + 1)}
                  backgroundColor={index === currentIndex ? tokens.color.components.steps.stepCurrentBg : ''}
                  color={index === currentIndex ? tokens.color.text.textDefault : tokens.color.text.textSubtle}
                />
              )}
              <Text type={index === currentIndex ? 'default' : 'subtle'} size="md" strong={index === currentIndex}>
                {label}
              </Text>
            </div>
            {index < stepLabels.length - 1 && <Tail width="32px" />}
          </div>
        ))}
      </div>
      <Button dataTestId="close-alert-wizard" type="text" onClick={onClose}>
        <CloseOutlined />
      </Button>
    </div>
  );
}
